import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Plus, Edit, Trash2, UserCheck, UserX } from 'lucide-react';
import { usersApi } from '../../api/endpoints/users';
import { queryClient } from '../../providers/QueryProvider';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate } from '../../utils/formatters';
import type { User } from '../../types/user';

const roleBadgeVariant: Record<string, 'purple' | 'blue' | 'gray'> = {
  admin: 'purple',
  operator: 'blue',
  viewer: 'gray',
};

interface UserFormData {
  username: string;
  email: string;
  password: string;
  role: string;
}

const emptyForm: UserFormData = { username: '', email: '', password: '', role: 'viewer' };

export function UsersPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form, setForm] = useState<UserFormData>(emptyForm);
  const [formError, setFormError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: { username: string; password: string; email?: string; role: string }) =>
      usersApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message || 'Failed to create user'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: Partial<{ username: string; email: string; role: string }> }) =>
      usersApi.update(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message || 'Failed to update user'),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => usersApi.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (userId: string) => usersApi.toggleActive(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const users = data?.users || [];

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (user: User) => {
    setEditingUser(user);
    setForm({ username: user.username, email: user.email || '', password: '', role: user.role });
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setForm(emptyForm);
    setFormError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!form.username.trim()) {
      setFormError('Username is required');
      return;
    }
    if (!editingUser && !form.password.trim()) {
      setFormError('Password is required');
      return;
    }

    if (editingUser) {
      updateMutation.mutate({
        userId: editingUser._id,
        payload: {
          username: form.username.trim(),
          email: form.email.trim() || undefined,
          role: form.role,
        },
      });
    } else {
      createMutation.mutate({
        username: form.username.trim(),
        password: form.password,
        email: form.email.trim() || undefined,
        role: form.role,
      });
    }
  };

  const handleDelete = (user: User) => {
    if (!window.confirm(`Delete user "${user.username}"? This cannot be undone.`)) return;
    deleteMutation.mutate(user._id);
  };

  const handleToggle = (user: User) => {
    toggleMutation.mutate(user._id);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading && !data) return <PageSpinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Users</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
            {users.length} user{users.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs"
        >
          <Plus size={14} />
          Add User
        </button>
      </div>

      {/* Users Table */}
      <Card>
        <Table>
          <Thead>
            <tr>
              <Th>Username</Th>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Last Login</Th>
              <Th>Actions</Th>
            </tr>
          </Thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {users.map((user) => {
              const initial = (user.username || '?')[0].toUpperCase();
              const variant = roleBadgeVariant[user.role] || 'gray';
              return (
                <Tr key={user._id}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-purple-600 dark:text-purple-400">{initial}</span>
                      </div>
                      <span className="font-medium text-gray-800 dark:text-gray-200">{user.username}</span>
                    </div>
                  </Td>
                  <Td>
                    <span className="text-gray-600 dark:text-gray-400">{user.email || '--'}</span>
                  </Td>
                  <Td>
                    <Badge variant={variant}>{user.role}</Badge>
                  </Td>
                  <Td>
                    <Badge variant={user.isActive ? 'green' : 'gray'} dot>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    <span className="text-gray-500 dark:text-gray-400">
                      {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(user)}
                        className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleToggle(user)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title={user.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {user.isActive ? <UserX size={14} /> : <UserCheck size={14} />}
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </Td>
                </Tr>
              );
            })}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      {/* Add / Edit Modal */}
      <Modal open={modalOpen} onClose={closeModal} title={editingUser ? 'Edit User' : 'Add User'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-3 py-2 rounded-md text-xs">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
              placeholder="Optional"
            />
          </div>

          {!editingUser && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
            >
              <option value="admin">Admin</option>
              <option value="operator">Operator</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={closeModal}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : editingUser ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
